<template>
    <Teleport to="body">
        <AmModal
            :open="open"
            size="chart"
            :title="title"
            :subtitle="subtitle"
            title-tag="h3"
            body-flush
            elevated
            @close="close"
        >
            <template #header-actions>
                <button
                    type="button"
                    class="chart-mode-btn"
                    :class="{ 'chart-mode-btn--active': top5Active }"
                    @click="onTop5Click"
                >
                    Топ-5
                </button>
            </template>

            <div class="chart-modal-body">
                <div class="chart-modal-main">
                    <CompareChartView
                        :payload="payload"
                        :view-config="localView"
                        :max-tooltip-rows="12"
                        :view-height="chartViewHeight"
                        tall
                    />
                </div>
                <aside class="chart-modal-legend">
                    <div class="legend-title">Легенда · клик — скрыть</div>
                    <ChartLegendPanel
                        :items="legendItems"
                        @toggle="onLegendToggle"
                    />
                </aside>
            </div>
        </AmModal>
    </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AmModal from '@/components/ui/AmModal.vue'
import CompareChartView from './CompareChartView.vue'
import ChartLegendPanel from './ChartLegendPanel.vue'
import {
    buildLegendItems,
    defaultChartViewConfig,
    isTop5PresetActive,
    normalizeChartViewConfig,
    toggleLegendKey,
    toggleTop5Preset,
} from './chartViewConfig'

const props = defineProps({
    open: { type: Boolean, default: false },
    payload: { type: Object, default: null },
    viewConfig: { type: Object, default: null },
    title: { type: String, default: 'График' },
    subtitle: { type: String, default: '' },
})

const emit = defineEmits(['close', 'update:view-config'])

const localView = ref(normalizeChartViewConfig(props.viewConfig || defaultChartViewConfig()))
const isMobile = ref(false)

watch(
    () => props.viewConfig,
    v => {
        localView.value = normalizeChartViewConfig(v || defaultChartViewConfig())
    },
    { deep: true },
)

const legendItems = computed(() =>
    buildLegendItems(props.payload, localView.value),
)

const top5Active = computed(() =>
    isTop5PresetActive(props.payload, localView.value),
)

const chartViewHeight = computed(() => (isMobile.value ? 260 : 400))

function updateMobileFlag() {
    if (typeof window === 'undefined') return
    isMobile.value = window.matchMedia('(max-width: 800px)').matches
}

function emitView() {
    emit('update:view-config', { ...localView.value })
}

function onLegendToggle(key) {
    localView.value = toggleLegendKey(props.payload, localView.value, key)
    emitView()
}

function onTop5Click() {
    localView.value = toggleTop5Preset(props.payload, localView.value)
    emitView()
}

function close() {
    emit('close')
}

function onKeydown(event) {
    if (event.key === 'Escape' && props.open) close()
}

let mobileMq = null

onMounted(() => {
    updateMobileFlag()
    if (typeof window !== 'undefined') {
        mobileMq = window.matchMedia('(max-width: 800px)')
        mobileMq.addEventListener('change', updateMobileFlag)
    }
})

onBeforeUnmount(() => {
    mobileMq?.removeEventListener('change', updateMobileFlag)
    if (typeof document !== 'undefined') {
        document.removeEventListener('keydown', onKeydown)
    }
})

watch(() => props.open, isOpen => {
    if (typeof document === 'undefined') return
    if (isOpen) {
        document.addEventListener('keydown', onKeydown)
    } else {
        document.removeEventListener('keydown', onKeydown)
    }
})
</script>

<style scoped>
.chart-mode-btn {
    padding: 6px 12px;
    border: 1px solid #E8E6E0;
    border-radius: 8px;
    background: white;
    font-size: 12px;
    font-weight: 600;
    color: #5F5E5A;
    cursor: pointer;
    font-family: inherit;
    line-height: 1.2;
}

.chart-mode-btn--active {
    background: #23282C;
    border-color: #23282C;
    color: white;
}

.chart-modal-body {
    display: grid;
    grid-template-columns: 1fr minmax(180px, 240px);
    gap: 16px;
    padding: 16px 22px 20px;
    flex: 1;
    min-height: 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
}

.chart-modal-main {
    min-width: 0;
}

.chart-modal-legend {
    border-left: 1px solid #F1EFE8;
    padding-left: 14px;
    max-height: 440px;
    overflow-y: auto;
}

.legend-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #888780;
    margin-bottom: 8px;
}

@media (max-width: 800px) {
    .chart-modal-body {
        grid-template-columns: 1fr;
        padding: 12px 14px;
        padding-bottom: max(16px, env(safe-area-inset-bottom));
    }

    .chart-modal-legend {
        border-left: none;
        border-top: 1px solid #F1EFE8;
        padding-left: 0;
        padding-top: 12px;
        max-height: min(220px, 35vh);
    }
}
</style>
